/**
 * Data Aggregation Utilities for Adaptive Profit Visualization
 * 
 * Provides utilities for fetching, transforming, and aggregating profit data
 * for chart rendering.
 */

import { DailyProfitSummary, HourlyProfitBreakdown, getHourlyProfit, getDailyProfitRange } from '../api/profit';
import { formatDateUTC } from './dateUtils';

/**
 * Chart data point structure used for rendering
 */
export interface ChartDataPoint {
  timestamp: string; // ISO 8601 date or datetime
  date: string; // Human-readable date
  net_profit: number;
  gross_profit: number;
  trade_count: number;
  fees_paid: number;
  period_type: 'hour' | 'day' | 'week' | 'month';
}

/**
 * Aggregated data for weekly/monthly views
 */
export interface AggregatedData extends ChartDataPoint {
  period_start: string;
  period_end: string;
  period_label: string; // e.g., "Week of Oct 21" or "October 2025"
  trading_days: number; // count of days with trades
}

/**
 * Transform hourly profit data to chart data points
 * @param hourlyData - Hourly profit breakdown from API
 * @param date - Date in YYYY-MM-DD format
 * @returns Array of chart data points
 */
export function transformHourlyToChartData(
  hourlyData: HourlyProfitBreakdown[],
  date: string
): ChartDataPoint[] {
  // Create a complete 24-hour array
  const chartData: ChartDataPoint[] = [];
  
  for (let hour = 0; hour < 24; hour++) {
    const existing = hourlyData.find(h => h.trade_hour === hour);
    
    if (existing) {
      chartData.push({
        timestamp: `${date}T${String(hour).padStart(2, '0')}:00:00Z`,
        date: formatHourLabel(hour),
        net_profit: existing.net_profit,
        gross_profit: existing.gross_profit,
        trade_count: existing.trade_count,
        fees_paid: existing.gross_profit - existing.net_profit,
        period_type: 'hour'
      });
    } else {
      chartData.push({
        timestamp: `${date}T${String(hour).padStart(2, '0')}:00:00Z`,
        date: formatHourLabel(hour),
        net_profit: 0,
        gross_profit: 0,
        trade_count: 0,
        fees_paid: 0,
        period_type: 'hour'
      });
    }
  }
  
  return chartData;
}

/**
 * Transform daily profit data to chart data points
 * @param dailyData - Daily profit summaries from API
 * @returns Array of chart data points
 */
export function transformDailyToChartData(
  dailyData: DailyProfitSummary[]
): ChartDataPoint[] {
  return dailyData.map(day => ({
    timestamp: `${day.trade_date}T00:00:00Z`,
    date: formatDateShort(day.trade_date),
    net_profit: day.net_profit,
    gross_profit: day.gross_profit,
    trade_count: day.trade_count,
    fees_paid: day.fees_paid,
    period_type: 'day' as const
  }));
}

/**
 * Aggregate daily data into weekly buckets
 * @param dailyData - Daily profit summaries
 * @returns Array of aggregated weekly data points
 */
export function aggregateToWeekly(
  dailyData: DailyProfitSummary[]
): AggregatedData[] {
  if (dailyData.length === 0) return [];
  
  // Sort by date
  const sorted = [...dailyData].sort((a, b) => a.trade_date.localeCompare(b.trade_date));
  
  // Group by ISO week (Monday start)
  const weekMap = new Map<string, DailyProfitSummary[]>();
  
  sorted.forEach(day => {
    const date = new Date(day.trade_date + 'T00:00:00Z');
    const weekStart = getWeekStart(date);
    const weekKey = formatDateUTC(weekStart);
    
    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, []);
    }
    weekMap.get(weekKey)!.push(day);
  });
  
  // Aggregate each week
  const result: AggregatedData[] = [];
  
  weekMap.forEach((days, weekStart) => {
    const weekEnd = new Date(weekStart + 'T00:00:00Z');
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
    
    const aggregated = days.reduce(
      (sum, day) => ({
        net_profit: sum.net_profit + day.net_profit,
        gross_profit: sum.gross_profit + day.gross_profit,
        trade_count: sum.trade_count + day.trade_count,
        fees_paid: sum.fees_paid + day.fees_paid,
        trading_days: sum.trading_days + (day.trade_count > 0 ? 1 : 0)
      }),
      { net_profit: 0, gross_profit: 0, trade_count: 0, fees_paid: 0, trading_days: 0 }
    );
    
    result.push({
      timestamp: weekStart + 'T00:00:00Z',
      date: formatWeekLabel(weekStart),
      period_start: weekStart,
      period_end: formatDateUTC(weekEnd),
      period_label: `Week of ${formatDateShort(weekStart)}`,
      net_profit: aggregated.net_profit,
      gross_profit: aggregated.gross_profit,
      trade_count: aggregated.trade_count,
      fees_paid: aggregated.fees_paid,
      trading_days: aggregated.trading_days,
      period_type: 'week'
    });
  });
  
  // Sort by date
  return result.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

/**
 * Aggregate daily data into monthly buckets
 * @param dailyData - Daily profit summaries
 * @returns Array of aggregated monthly data points
 */
export function aggregateToMonthly(
  dailyData: DailyProfitSummary[]
): AggregatedData[] {
  if (dailyData.length === 0) return [];
  
  // Group by month
  const monthMap = new Map<string, DailyProfitSummary[]>();
  
  dailyData.forEach(day => {
    const monthKey = day.trade_date.substring(0, 7); // YYYY-MM
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, []);
    }
    monthMap.get(monthKey)!.push(day);
  });
  
  // Aggregate each month
  const result: AggregatedData[] = [];
  
  monthMap.forEach((days, monthKey) => {
    const [year, month] = monthKey.split('-').map(Number);
    const monthStart = new Date(Date.UTC(year, month - 1, 1));
    const monthEnd = new Date(Date.UTC(year, month, 0)); // Last day of month
    
    const aggregated = days.reduce(
      (sum, day) => ({
        net_profit: sum.net_profit + day.net_profit,
        gross_profit: sum.gross_profit + day.gross_profit,
        trade_count: sum.trade_count + day.trade_count,
        fees_paid: sum.fees_paid + day.fees_paid,
        trading_days: sum.trading_days + (day.trade_count > 0 ? 1 : 0)
      }),
      { net_profit: 0, gross_profit: 0, trade_count: 0, fees_paid: 0, trading_days: 0 }
    );
    
    result.push({
      timestamp: formatDateUTC(monthStart) + 'T00:00:00Z',
      date: formatMonthLabel(monthStart),
      period_start: formatDateUTC(monthStart),
      period_end: formatDateUTC(monthEnd),
      period_label: formatMonthLabel(monthStart),
      net_profit: aggregated.net_profit,
      gross_profit: aggregated.gross_profit,
      trade_count: aggregated.trade_count,
      fees_paid: aggregated.fees_paid,
      trading_days: aggregated.trading_days,
      period_type: 'month'
    });
  });
  
  // Sort by date
  return result.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

/**
 * Get the Monday of the week containing the given date
 * @param date - Date object
 * @returns Date object for the Monday of that week
 */
function getWeekStart(date: Date): Date {
  const dayOfWeek = date.getUTCDay();
  const diff = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek; // Monday = 1, Sunday = 0
  const monday = new Date(date);
  monday.setUTCDate(date.getUTCDate() + diff);
  return monday;
}

/**
 * Format hour as human-readable label (e.g., "12am", "1pm")
 * @param hour - Hour (0-23)
 * @returns Formatted label
 */
function formatHourLabel(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}${period}`;
}

/**
 * Format date as short label (e.g., "Oct 27")
 * @param dateStr - Date in YYYY-MM-DD format
 * @returns Formatted label
 */
function formatDateShort(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

/**
 * Format week label (e.g., "Oct 21")
 * @param dateStr - Date in YYYY-MM-DD format
 * @returns Formatted label
 */
function formatWeekLabel(dateStr: string): string {
  return formatDateShort(dateStr);
}

/**
 * Format month label (e.g., "October 2025")
 * @param date - Date object
 * @returns Formatted label
 */
function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
}

/**
 * Fill gaps in daily data with zero values
 * @param data - Daily profit data (may have gaps)
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Complete daily data with zero-filled gaps
 */
export function fillDailyGaps(
  data: DailyProfitSummary[],
  startDate: string,
  endDate: string
): DailyProfitSummary[] {
  const result: DailyProfitSummary[] = [];
  const dataMap = new Map(data.map(d => [d.trade_date, d]));
  
  const start = new Date(startDate + 'T00:00:00Z');
  const end = new Date(endDate + 'T00:00:00Z');
  
  for (let date = new Date(start); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    const dateStr = formatDateUTC(date);
    
    if (dataMap.has(dateStr)) {
      result.push(dataMap.get(dateStr)!);
    } else {
      result.push({
        trade_date: dateStr,
        product_id: undefined,
        gross_profit: 0,
        net_profit: 0,
        trade_count: 0,
        fees_paid: 0
      });
    }
  }
  
  return result;
}
